import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import {
  NOMBRE_TIPO,
  useAtencion,
  useAvisos,
  useBorrarEvento,
  useEventos,
  useGuardarAvisos,
  useGuardarEvento,
  useProbarAvisos,
  type Evento,
  type TipoEvento,
} from '../api/agenda';
import { useProperties } from '../api/properties';
import { useClients } from '../api/clients';
import { useMe } from '../api/me';
import { AyudaGuia } from '../components/Ayuda';
import AvisosCorreo from '../components/AvisosCorreo';

// ── Fechas ────────────────────────────────────────────────────────
// Todo con la fecha LOCAL del navegador, por partes: `toISOString()` pasa a UTC
// y a última hora del día devolvería el día siguiente.

const dos = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
const mesDe = (d: Date) => `${d.getFullYear()}-${dos(d.getMonth() + 1)}`;
const desplazaMes = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function tituloMes(d: Date) {
  const s = d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function diaLargo(isoFecha: string) {
  const [y, m, d] = isoFecha.split('-').map(Number);
  const s = new Date(y, m - 1, d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Las semanas del mes, de lunes a domingo, con los días de fuera a null. */
function semanas(mes: Date): (Date | null)[][] {
  const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
  const dias = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
  const hueco = (primero.getDay() + 6) % 7; // lunes = 0
  const celdas: (Date | null)[] = Array(hueco).fill(null);
  for (let d = 1; d <= dias; d++) celdas.push(new Date(mes.getFullYear(), mes.getMonth(), d));
  while (celdas.length % 7) celdas.push(null);
  return Array.from({ length: celdas.length / 7 }, (_, i) => celdas.slice(i * 7, i * 7 + 7));
}

const COLOR: Record<TipoEvento, string> = {
  visita: 'bg-brand/15 text-brand',
  cita: 'bg-deep/10 text-deep',
  tarea: 'bg-amber-100 text-amber-800',
};

// ── Vista ─────────────────────────────────────────────────────────

export default function AgendaView() {
  const hoy = iso(new Date());
  const [mes, setMes] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dia, setDia] = useState(hoy);
  const [editando, setEditando] = useState<Partial<Evento> | null>(null);

  const meses = [mesDe(desplazaMes(mes, -1)), mesDe(mes), mesDe(desplazaMes(mes, 1))];
  const { data: eventos, isLoading, isError, refetch } = useEventos(meses);
  const { data: me } = useMe();
  const esAdmin = Boolean(me?.groups.includes('admin'));

  const porDia = useMemo(() => {
    const m = new Map<string, Evento[]>();
    for (const e of eventos ?? []) m.set(e.fecha, [...(m.get(e.fecha) ?? []), e]);
    return m;
  }, [eventos]);
  const atrasadas = (eventos ?? []).filter((e) => e.tipo === 'tarea' && !e.hecho && e.fecha < hoy);

  const irAHoy = () => {
    const d = new Date();
    setMes(new Date(d.getFullYear(), d.getMonth(), 1));
    setDia(hoy);
  };

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight text-deep">Agenda</h1>
            <AyudaGuia seccion="agenda" />
          </div>
          <p className="mt-1 text-sm text-ink/55">
            Visitas, citas y tareas, y lo que se está quedando sin atender.
          </p>
        </div>
        <button
          onClick={() => setEditando({ tipo: 'visita', fecha: dia })}
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition hover:bg-deep"
        >
          <Plus size={16} /> Nuevo evento
        </button>
      </div>

      <RequiereAtencion />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* Calendario */}
        <section className="rounded-2xl border border-deep/10 bg-surface p-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setMes(desplazaMes(mes, -1))} aria-label="Mes anterior" className="rounded-full p-1.5 text-ink/60 hover:bg-soft hover:text-deep">
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-3">
              <h2 className="text-base font-extrabold text-deep">{tituloMes(mes)}</h2>
              <button onClick={irAHoy} className="rounded-full border border-deep/15 px-3 py-0.5 text-xs font-bold text-ink/60 hover:border-brand hover:text-brand">
                Hoy
              </button>
            </div>
            <button onClick={() => setMes(desplazaMes(mes, 1))} aria-label="Mes siguiente" className="rounded-full p-1.5 text-ink/60 hover:bg-soft hover:text-deep">
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[0.68rem] font-bold uppercase tracking-wider text-ink/40">
            {DIAS_SEMANA.map((d) => <span key={d}>{d}</span>)}
          </div>
          {isLoading ? (
            <p className="py-16 text-center text-sm text-ink/50">Cargando…</p>
          ) : isError ? (
            <div className="py-12 text-center">
              <p className="text-sm font-bold text-red-700">No se ha podido cargar la agenda</p>
              <button onClick={() => refetch()} className="mt-3 rounded-full bg-red-600 px-4 py-1.5 text-sm font-bold text-white">Reintentar</button>
            </div>
          ) : (
            <div className="mt-1 space-y-1">
              {semanas(mes).map((semana, i) => (
                <div key={i} className="grid grid-cols-7 gap-1">
                  {semana.map((d, j) => {
                    if (!d) return <span key={j} />;
                    const f = iso(d);
                    const lista = porDia.get(f) ?? [];
                    return (
                      <button
                        key={j}
                        onClick={() => setDia(f)}
                        className={clsx(
                          'flex min-h-[3.25rem] flex-col items-stretch rounded-xl border p-1 text-left transition md:min-h-[4.75rem]',
                          f === dia ? 'border-brand bg-leaf/20' : 'border-transparent hover:bg-soft',
                        )}
                      >
                        <span className={clsx(
                          'mx-auto flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold md:mx-0',
                          f === hoy ? 'bg-deep text-white' : 'text-ink/70',
                        )}>
                          {d.getDate()}
                        </span>
                        {/* Móvil: puntos. Escritorio: los dos primeros, con texto. */}
                        {lista.length > 0 && (
                          <span className="mt-1 flex justify-center gap-0.5 md:hidden">
                            {lista.slice(0, 3).map((e) => (
                              <span key={e.event_id} className={clsx('h-1.5 w-1.5 rounded-full', e.tipo === 'tarea' ? 'bg-amber-500' : e.tipo === 'cita' ? 'bg-deep' : 'bg-brand')} />
                            ))}
                          </span>
                        )}
                        <span className="mt-1 hidden space-y-0.5 md:block">
                          {lista.slice(0, 2).map((e) => (
                            <span key={e.event_id} className={clsx('block truncate rounded px-1 text-[0.65rem] font-semibold', COLOR[e.tipo], e.hecho && 'line-through opacity-60')}>
                              {e.hora ? `${e.hora} ` : ''}{e.titulo}
                            </span>
                          ))}
                          {lista.length > 2 && <span className="block px-1 text-[0.65rem] text-ink/45">+{lista.length - 2} más</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* El día elegido */}
        <section>
          <div className="flex items-baseline justify-between border-b border-deep/10 pb-2">
            <h2 className="text-sm font-bold uppercase tracking-wider text-ink/55">{diaLargo(dia)}</h2>
            <button onClick={() => setEditando({ tipo: 'visita', fecha: dia })} className="text-xs font-bold text-brand hover:text-deep">
              + Añadir a este día
            </button>
          </div>
          <ListaEventos eventos={porDia.get(dia) ?? []} onEditar={setEditando} vacio="Nada apuntado este día." />

          {atrasadas.length > 0 && (
            <>
              <h2 className="mt-6 border-b border-deep/10 pb-2 text-sm font-bold uppercase tracking-wider text-amber-700">
                Tareas sin hacer de días anteriores · {atrasadas.length}
              </h2>
              <ListaEventos eventos={atrasadas} onEditar={setEditando} conFecha vacio="" />
            </>
          )}
        </section>
      </div>

      {esAdmin && <AvisosAgenda />}

      {editando && <EventoModal evento={editando} onClose={() => setEditando(null)} />}
    </div>
  );
}

// ── Lo que requiere atención ──────────────────────────────────────

function RequiereAtencion() {
  const { data } = useAtencion();
  if (!data) return null;
  const bloques = [
    {
      titulo: 'Solicitudes sin responder', a: '/clientes', total: data.solicitudes.length,
      filas: data.solicitudes.map((s) => `${s.nombre} · ${s.dias} días`),
    },
    {
      titulo: 'Contratos que vencen', a: '/viviendas', total: data.contratos.length,
      filas: data.contratos.map((c) => `${c.vivienda} · ${c.dias < 0 ? `venció hace ${-c.dias} días` : `en ${c.dias} días`}`),
    },
    {
      titulo: 'Incidencias sin abrir', a: '/incidencias', total: data.incidencias.length,
      filas: data.incidencias.map((i) => `${i.vivienda} · ${i.dias} días`),
    },
  ];
  const nada = bloques.every((b) => b.total === 0);

  return (
    <section className={clsx('mt-5 rounded-2xl border p-4', nada ? 'border-deep/10 bg-surface' : 'border-amber-300 bg-amber-50/60')}>
      <h2 className={clsx('flex items-center gap-2 text-sm font-extrabold', nada ? 'text-deep' : 'text-amber-900')}>
        {nada ? <Check size={16} className="text-brand" /> : <AlertTriangle size={16} />}
        {nada ? 'Nada pendiente: todo está atendido' : 'Requiere atención'}
      </h2>
      {!nada && (
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {bloques.map((b) => (
            <Link key={b.titulo} to={b.a} className="rounded-xl bg-surface px-3 py-2 transition hover:ring-1 hover:ring-amber-300">
              <p className="text-xs font-bold uppercase tracking-wider text-ink/50">{b.titulo}</p>
              <p className="text-2xl font-extrabold text-deep">{b.total}</p>
              {b.filas.slice(0, 3).map((f) => (
                <p key={f} className="truncate text-xs text-ink/60">{f}</p>
              ))}
              {b.total > 3 && <p className="text-xs text-ink/45">y {b.total - 3} más</p>}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Lista de eventos ──────────────────────────────────────────────

function ListaEventos({ eventos, onEditar, conFecha, vacio }: {
  eventos: Evento[]; onEditar: (e: Evento) => void; conFecha?: boolean; vacio: string;
}) {
  const guardar = useGuardarEvento();
  const { data: viviendas } = useProperties();
  const { data: clientes } = useClients();
  const direccion = (id?: string) => viviendas?.find((v) => v.property_id === id)?.direccion;
  const nombre = (id?: string) => clientes?.find((c) => c.client_id === id)?.nombre;

  if (!eventos.length) return vacio ? <p className="mt-3 text-sm text-ink/45">{vacio}</p> : null;
  return (
    <ul className="mt-3 space-y-2">
      {eventos.map((e) => (
        <li key={e.event_id} className="flex items-start gap-3 rounded-2xl border border-deep/10 bg-surface px-3 py-2.5">
          <button
            onClick={() => guardar.mutate({ event_id: e.event_id, hecho: !e.hecho })}
            aria-label={e.hecho ? 'Marcar como pendiente' : 'Marcar como hecho'}
            title={e.hecho ? 'Hecho' : 'Marcar como hecho'}
            className={clsx(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition',
              e.hecho ? 'border-brand bg-brand text-white' : 'border-deep/25 hover:border-brand',
            )}
          >
            {e.hecho && <Check size={13} strokeWidth={3} />}
          </button>
          <div className="min-w-0 flex-1">
            <p className={clsx('text-sm font-bold text-deep', e.hecho && 'text-ink/45 line-through')}>
              {conFecha && <span className="font-semibold text-amber-700">{e.fecha.split('-').reverse().join('/')} · </span>}
              {e.hora && <span className="text-ink/60">{e.hora} · </span>}
              {e.titulo}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink/55">
              <span className={clsx('rounded-full px-2 py-0.5 font-bold', COLOR[e.tipo])}>{NOMBRE_TIPO[e.tipo]}</span>
              {direccion(e.property_id) && <span>{direccion(e.property_id)}</span>}
              {nombre(e.client_id) && <span>{nombre(e.client_id)}</span>}
            </div>
            {e.notas && <p className="mt-1 whitespace-pre-line text-xs text-ink/60">{e.notas}</p>}
          </div>
          <button onClick={() => onEditar(e)} aria-label="Editar evento" title="Editar" className="rounded-full p-1.5 text-ink/40 hover:bg-soft hover:text-deep">
            <Pencil size={15} />
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── Nuevo / editar ────────────────────────────────────────────────

function EventoModal({ evento, onClose }: { evento: Partial<Evento>; onClose: () => void }) {
  const guardar = useGuardarEvento();
  const borrar = useBorrarEvento();
  const { data: viviendas } = useProperties();
  const { data: clientes } = useClients();
  const [f, setF] = useState<Partial<Evento>>(evento);
  const nuevo = !evento.event_id;
  const error = (guardar.error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;

  const submit = () => {
    if (!f.titulo?.trim() || !f.fecha) return;
    guardar.mutate(
      {
        event_id: f.event_id, tipo: f.tipo, titulo: f.titulo, fecha: f.fecha,
        hora: f.hora ?? '', property_id: f.property_id ?? '', client_id: f.client_id ?? '', notas: f.notas ?? '',
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-deep/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-deep">{nuevo ? 'Nuevo evento' : 'Editar evento'}</h2>
          <button onClick={onClose} aria-label="Cerrar" className="text-ink/40 hover:text-deep"><X size={20} /></button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Campo label="Tipo">
            <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value as TipoEvento })} className={INPUT}>
              <option value="visita">Visita</option>
              <option value="cita">Cita</option>
              <option value="tarea">Tarea</option>
            </select>
          </Campo>
          <Campo label="Título *">
            <input value={f.titulo ?? ''} onChange={(e) => setF({ ...f, titulo: e.target.value })} placeholder="Enseñar el piso a Ana" className={INPUT} />
          </Campo>
          <Campo label="Fecha *">
            <input type="date" value={f.fecha ?? ''} onChange={(e) => setF({ ...f, fecha: e.target.value })} className={INPUT} />
          </Campo>
          <Campo label="Hora">
            <input type="time" value={f.hora ?? ''} onChange={(e) => setF({ ...f, hora: e.target.value })} className={INPUT} />
          </Campo>
          <Campo label="Vivienda">
            <select value={f.property_id ?? ''} onChange={(e) => setF({ ...f, property_id: e.target.value })} className={INPUT}>
              <option value="">—</option>
              {(viviendas ?? []).map((v) => <option key={v.property_id} value={v.property_id}>{v.direccion}</option>)}
            </select>
          </Campo>
          <Campo label="Cliente">
            <select value={f.client_id ?? ''} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={INPUT}>
              <option value="">—</option>
              {(clientes ?? []).map((c) => <option key={c.client_id} value={c.client_id}>{c.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Notas" full>
            <textarea value={f.notas ?? ''} onChange={(e) => setF({ ...f, notas: e.target.value })} rows={3} className={INPUT} />
          </Campo>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex items-center justify-between gap-2">
          {!nuevo ? (
            <button
              onClick={() => borrar.mutate(evento.event_id!, { onSuccess: onClose })}
              className="text-sm font-bold text-red-600 hover:text-red-700"
            >
              Eliminar
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-bold text-ink/55 hover:text-deep">Cancelar</button>
            <button
              onClick={submit}
              disabled={guardar.isPending || !f.titulo?.trim() || !f.fecha}
              className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-deep disabled:opacity-50"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Avisos por correo (solo admin) ────────────────────────────────

function AvisosAgenda() {
  const { data } = useAvisos(true);
  const guardar = useGuardarAvisos();
  const probar = useProbarAvisos();
  return (
    <AvisosCorreo
      titulo="Avisos por correo"
      interruptor="Enviar el resumen cada mañana"
      boton="Guardar avisos"
      avisos={data}
      guardar={guardar}
      probar={probar}
    >
      Cada mañana a las 8, un correo con las visitas y tareas del día, las visitas de mañana y lo que
      requiere atención. Si un día no hay nada, no se manda.
    </AvisosCorreo>
  );
}

// ── Piezas ────────────────────────────────────────────────────────

const INPUT =
  'w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep outline-none placeholder:text-ink/40 focus:border-brand';

function Campo({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <label className={clsx('block', full && 'sm:col-span-2')}>
      <span className="mb-1 block text-xs font-bold text-ink/50">{label}</span>
      {children}
    </label>
  );
}
